import React, { useState, useEffect } from 'react';
import { Modal, Backdrop, Box, Typography, CircularProgress, Button, Fade } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export interface LoadingModalProps {
  open: boolean;
  status: 'loading' | 'success' | 'error' | 'idle' | 'empty-export';
  onClose: () => void;
  loadingTitle?: string;
  successTitle?: string;
  errorTitle?: string;
  emptyExportTitle?: string;
  loadingMessages?: string[];
  successMessage?: string;
  errorMessage?: string;
  emptyExportMessage?: string;
  autoCloseDelay?: number;
  downloadCallback?: (() => void) | null;
}

const defaultLoadingMessages = ['Processing...', 'Please wait...', 'Almost ready...', 'Finalizing...'];

export const LoadingModal: React.FC<LoadingModalProps> = ({
  open,
  status,
  onClose,
  loadingTitle = 'Processing',
  successTitle = 'Completed',
  errorTitle = 'Failed',
  emptyExportTitle = 'No Data Found',
  loadingMessages = defaultLoadingMessages,
  successMessage = 'Operation completed successfully!',
  errorMessage = 'Operation failed. Please try again.',
  emptyExportMessage = 'No data found for the selected criteria. Do you still want to download an empty file?',
  autoCloseDelay = 3000,
  downloadCallback,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [autoCloseTimer, setAutoCloseTimer] = useState<number | null>(null);

  useEffect(() => {
    let interval: number;

    if (status === 'loading') {
      interval = window.setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 1500);
    }

    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [status, loadingMessages.length]);

  useEffect(() => {
    if (status === 'success' || status === 'error') {
      const timer = window.setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      setAutoCloseTimer(timer);
    }

    return () => {
      if (autoCloseTimer) window.clearTimeout(autoCloseTimer);
    };
  }, [status, onClose, autoCloseDelay]);

  const handleClose = () => {
    if (autoCloseTimer) window.clearTimeout(autoCloseTimer);
    onClose();
  };

  const getContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: <CircularProgress size={60} />,
          title: loadingTitle,
          message: loadingMessages[messageIndex],
          showButton: false,
          showDownloadButton: false,
        };
      case 'success':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />,
          title: successTitle,
          message: successMessage,
          showButton: true,
          showDownloadButton: false,
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ fontSize: 60, color: 'error.main' }} />,
          title: errorTitle,
          message: errorMessage,
          showButton: true,
          showDownloadButton: false,
        };
      case 'empty-export':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 60, color: 'warning.main' }} />,
          title: emptyExportTitle,
          message: emptyExportMessage,
          showButton: true,
          showDownloadButton: true,
        };
      default:
        return null;
    }
  };

  const content = getContent();
  if (!content) return null;

  return (
    <Modal
      open={open}
      onClose={status === 'loading' ? undefined : handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: 'center',
            outline: 'none',
          }}
        >
          <Box sx={{ mb: 2 }}>{content.icon}</Box>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {content.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {content.message}
          </Typography>

          {content.showButton && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {content.showDownloadButton && downloadCallback && (
                <Button
                  variant="contained"
                  onClick={() => {
                    downloadCallback();
                    handleClose?.();
                  }}
                  sx={{ minWidth: 120 }}
                >
                  Download Empty File
                </Button>
              )}
              <Button variant={content.showDownloadButton ? 'outlined' : 'contained'} onClick={handleClose} sx={{ minWidth: 100 }}>
                {content.showDownloadButton ? 'Cancel' : 'OK'}
              </Button>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};
